import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Video, Headphones, Globe, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function LearnSomethingNewGuide() {
  const navigate = useNavigate();

  const learningOptions = [
    { icon: Video, title: "LinkedIn Learning", desc: "Thousands of professional courses, often free through libraries", color: "blue" },
    { icon: Headphones, title: "Podcasts", desc: "Industry-specific shows you can learn from during commute", color: "purple" },
    { icon: Globe, title: "TED Talks", desc: "18-minute talks on innovation, leadership, and ideas", color: "red" },
    { icon: BookOpen, title: "Industry Articles", desc: "Harvard Business Review, industry publications, newsletters", color: "green" },
  ];

  const topicIdeas = [
    "Leadership & Management Skills",
    "Communication & Presentation",
    "Data Analysis & Visualization",
    "Project Management Methodologies",
    "Negotiation & Influence",
    "Industry Trends & Innovations",
    "AI & Technology in Your Field",
    "Personal Branding"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Learn Something New</h1>
          <p className="text-lg text-secondary-600">Invest in your professional growth today</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Continuous Learning Matters</h2>
          <p className="text-secondary-700 mb-4">
            The half-life of professional skills is now about 5 years—meaning half of what you know becomes outdated. Continuous learning isn't optional; it's survival. Plus, learning new things keeps your brain sharp and your career exciting.
          </p>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <p className="text-emerald-800 font-medium">
              📈 Employees who spend time learning are 47% less likely to be stressed and 39% more likely to feel productive.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Learning Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {learningOptions.map((option, i) => (
              <div key={i} className={`p-4 bg-${option.color}-50 rounded-xl border border-${option.color}-200`}>
                <div className="flex items-center gap-3 mb-2">
                  <option.icon className={`w-6 h-6 text-${option.color}-600`} />
                  <h3 className="font-bold text-secondary-900">{option.title}</h3>
                </div>
                <p className="text-secondary-600 text-sm">{option.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Topic Ideas</h2>
          <p className="text-secondary-600 mb-4">Not sure what to learn? Consider these areas:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {topicIdeas.map((topic, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                <span className="text-secondary-700">{topic}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-amber-500" />
            Today's 30-60 Minute Challenge
          </h2>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <span className="text-secondary-700">Choose ONE topic that would benefit your career</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <span className="text-secondary-700">Find a course, talk, or article on that topic</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <span className="text-secondary-700">Set a timer and give it your full attention</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <span className="text-secondary-700">Take notes on one key insight you can apply</span>
            </li>
          </ol>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What did I learn today? How can I apply this in the next week? What do I want to learn more about?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default LearnSomethingNewGuide;
